import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { unionSaleApi } from "../apiCall";
import { normalizeApiError, buildRangeKey } from "../utils/unionSale";

class UnionSaleError extends Error {
    constructor(message, statusCode) {
        super(message);
        this.name = "UnionSaleError";
        this.statusCode = statusCode;
    }
}
const unwrap = (response) => {
    if (response?.status === "success" && response?.data) return response.data;
    if (!response) {
        throw new UnionSaleError("Your session could not be verified. Please login again.", 401);
    }
    throw new UnionSaleError(response.message, response.statusCode);
};

const useApiResource = (request, enabled = true) => {
    const [state, setState] = useState({ data: null, loading: enabled, error: null });
    const runIdRef = useRef(0);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const run = useCallback(
        (extra) => {
            if (!enabled) {
                setState({ data: null, loading: false, error: null });
                return;
            }
            runIdRef.current += 1;
            const runId = runIdRef.current;
            setState({ data: null, loading: true, error: null });

            const isCurrent = () => mountedRef.current && runIdRef.current === runId;

            request(extra)
                .then((data) => {
                    if (isCurrent()) setState({ data, loading: false, error: null });
                })
                .catch((error) => {
                    if (isCurrent()) {
                        setState({ data: null, loading: false, error: normalizeApiError(error) });
                    }
                });
        },
        [request, enabled]
    );

    useEffect(() => {
        run();
    }, [run]);

    const refresh = useCallback(() => run({ refresh: true }), [run]);

    return { ...state, refresh };
};

const sessionCache = { scopes: null, distributors: null };

const loadOnce = (key, fetcher) => {
    if (!sessionCache[key]) {
        sessionCache[key] = fetcher()
            .then(unwrap)
            .catch((error) => {
                sessionCache[key] = null;
                throw error;
            });
    }
    return sessionCache[key];
};

export const useUnionSaleScopes = () => {
    const request = useCallback(
        () => loadOnce("scopes", unionSaleApi.fetchUnionSaleScopes),
        []
    );
    return useApiResource(request);
};

export const useUnionSaleDistributors = () => {
    const request = useCallback(
        () => loadOnce("distributors", () => unionSaleApi.fetchUnionSaleDistributors({ activeOnly: true })),
        []
    );
    return useApiResource(request);
};

export const useUnionSaleOverview = ({
    fromDate,
    toDate,
    scopeId,
    unionId,
    district,
    distributor,
    productGroupBy,
    productTopN,
}) => {
    const request = useCallback(
        (extra) =>
            unionSaleApi
                .fetchUnionSaleOverview({
                    fromDate,
                    toDate,
                    scopeId,
                    unionId,
                    district,
                    distributor,
                    productGroupBy,
                    productTopN,
                    ...extra,
                })
                .then(unwrap),
        [fromDate, toDate, scopeId, unionId, district, distributor, productGroupBy, productTopN]
    );
    return useApiResource(request, Boolean(fromDate && toDate));
};

export const useUnionSaleUnionWise = ({ fromDate, toDate, scopeId, unionId, district, distributor }) => {
    const request = useCallback(
        (extra) =>
            unionSaleApi
                .fetchUnionSaleUnionWise({ fromDate, toDate, scopeId, unionId, district, distributor, ...extra })
                .then(unwrap),
        [fromDate, toDate, scopeId, unionId, district, distributor]
    );
    return useApiResource(request, Boolean(fromDate && toDate));
};

export const useUnionSaleDistributorMap = ({
    fromDate,
    toDate,
    scopeId,
    unionId,
    district,
    enabled = true,
}) => {
    const request = useCallback(
        () =>
            unionSaleApi
                .fetchUnionSaleDistributorMap({ fromDate, toDate, scopeId, unionId, district })
                .then(unwrap),
        [fromDate, toDate, scopeId, unionId, district]
    );
    return useApiResource(request, enabled && Boolean(fromDate && toDate));
};

const uniqueRangeKeys = (ranges) => {
    const keys = (ranges ?? [])
        .filter((range) => range?.fromDate && range?.toDate)
        .map(buildRangeKey);
    return [...new Set(keys)].sort((a, b) => a.localeCompare(b));
};

export const useUnionSaleTrends = ({ ranges, scopeId, unionId }) => {
    const rangeSignature = useMemo(() => uniqueRangeKeys(ranges).join(","), [ranges]);

    const request = useCallback(() => {
        const keys = rangeSignature ? rangeSignature.split(",") : [];
        return Promise.all(
            keys.map((key) => {
                const [fromDate, toDate] = key.split("|");
                return unionSaleApi
                    .fetchUnionSaleTrend({ fromDate, toDate, scopeId, unionId })
                    .then(unwrap)
                    .then((data) => [key, data]);
            })
        ).then((entries) => Object.fromEntries(entries));
    }, [rangeSignature, scopeId, unionId]);

    return useApiResource(request, Boolean(rangeSignature));
};
